import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid2,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import moment from "moment";
import {
  CaretLeft,
  CaretRight,
  Check,
  Circle,
  LightbulbFilament,
  Megaphone,
  Smiley,
  X,
} from "phosphor-react";
import { useEffect, useState, useRef, useCallback } from "react";
import { fget } from "../../../API/callsAPI.js";
import AudioControls from "./AudioControls.jsx";
import VRSessionTranscript from "./VRSessionTranscript.jsx";
import VersionVideoPlayer from "./VersionVideoPlayer.jsx";
import NoResultFound from "../../Utilities/NoResultFound/NoResultFound.jsx";

function VRSessionModal({ closeModal, session }) {
  const sessionId = session.id;
  const [currentSession, setCurrentSession] = useState(null);
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [currentAudioUrl, setCurrentAudioUrl] = useState(null);
  const audioRef = useRef(null);
  const videoPlayerRef = useRef(null);
  const allFilters = [
    {
      id: 1,
      name: "Corrections",
      icon: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            backgroundColor: "#00FF444D",
          }}
        >
          <Check size={14} color="#00FF44" weight="fill" />
        </Box>
      ),
      count: 22,
    },
    {
      id: 2,
      name: "Appreciations",
      icon: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            background: "#00EEFF4D",
          }}
        >
          <Smiley size={14} color="#00EEFF" />
        </Box>
      ),
      count: 22,
    },
    {
      id: 3,
      name: "Suggestions",
      icon: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            background: "#77FF004D",
          }}
        >
          <Megaphone size={14} color="#77FF00" weight="fill" />
        </Box>
      ),
      count: 22,
    },
    {
      id: 4,
      name: "Issues",
      icon: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            background: "#FF00004D",
            color: "#FF0000",
            fontSize: "14px",
          }}
        >
          !
        </Box>
      ),
      count: 22,
    },
    {
      id: 5,
      name: "Questions",
      icon: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            background: "#FFEA004D",
            color: "#FFEA00",
            fontSize: "14px",
          }}
        >
          ?
        </Box>
      ),
      count: 22,
    },
    {
      id: 6,
      name: "Ideas",
      icon: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            background: "#72A3FF4D",
          }}
        >
          <LightbulbFilament size={14} color="#72A3FF" />
        </Box>
      ),
      count: 22,
    },
  ];

  const scrollToComment = (versionId) => {
    const version = document.getElementById(`version-${versionId}`);
    if (version) {
      version.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (currentVersion && currentVersion.linked_transcriptions.length > 0) {
      scrollToComment(currentVersion.id);
    }
  }, [currentVersion]);

  const getSession = async () => {
    try {
      const res = await fget({
        url: `vr/session/${sessionId}/`,
      });
      setCurrentSession(res.data);
      setCurrentAudioUrl(
        Object.entries(res.data.file_urls).find(([key]) =>
          key.endsWith(".wav"),
        )?.[1] || null,
      );
    } catch (error) {
      console.log(error);
    }
  };

  const getSessionTrackables = async () => {
    try {
      const res = await fget({
        url: `vr/session/${sessionId}/trackables/`,
      });

      // Merge linked_annotations into linked_transcriptions based on audio_timestamp
      const mergeAnnotationsWithTranscriptions = (version) => {
        // Convert timestamp to seconds for sorting
        const timestampToSeconds = (timestamp) => {
          if (typeof timestamp === "number") return timestamp;
          if (typeof timestamp === "string") {
            const parts = timestamp.split(":");
            if (parts.length === 3) {
              // Format: "hh:mm:ss"
              return (
                parseInt(parts[0]) * 3600 +
                parseInt(parts[1]) * 60 +
                parseInt(parts[2])
              );
            }
            if (parts.length === 2) {
              // Format: "mm:ss"
              return parseInt(parts[0]) * 60 + parseInt(parts[1]);
            }
          }
          return 0;
        };

        // Combine transcriptions and annotations
        const combined = [];

        // Add transcriptions
        if (version.linked_transcriptions) {
          version.linked_transcriptions.forEach((transcription) => {
            combined.push({
              ...transcription,
              type: "transcription",
              audio_timestamp_seconds: timestampToSeconds(transcription.start),
            });
          });
        }

        // Add annotations
        if (version.linked_annotations) {
          version.linked_annotations.forEach((annotation) => {
            combined.push({
              ...annotation,
              type: "annotation",
              audio_timestamp_seconds: timestampToSeconds(
                annotation.sessionTimestamp,
              ),
            });
          });
        }
        // Sort by audio timestamp
        combined.sort(
          (a, b) => a.audio_timestamp_seconds - b.audio_timestamp_seconds,
        );

        return combined;
      };

      // Process each version to merge annotations with transcriptions
      const processedData = res.data.results.map((version) => ({
        ...version,
        linked_transcriptions: mergeAnnotationsWithTranscriptions(version),
      }));
      setVersions(processedData);
      setCurrentVersion(processedData[0]);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getSession();
    getSessionTrackables();
  }, []);

  if (versions.length === 0 && currentVersion === null) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress size={20} color="primary" />
      </Box>
    );
  }

  if (versions.length === 0) {
    return (
      <Box
        sx={{
          height: "60vh",
          width: "60vw",
          borderRadius: "20px",
          backgroundColor: "#1B1D28",
          padding: "24px",
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
            height: "38px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              fontSize: "18px",
            }}
          ></div>
          <IconButton
            onClick={closeModal}
            sx={{
              "&:hover": {
                backgroundColor: "rgba(249, 249, 249, 0.06)",
              },
            }}
          >
            <X size={20} color="#F9F9F9" />
          </IconButton>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <NoResultFound />
        </Box>
      </Box>
    );
  }

  const seekAudio = (timeInSeconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = timeInSeconds;
    }
  };

  return (
    <Box
      sx={{
        height: "calc(100vh - 33px)",
        width: "calc(100vw - 33px)",
        borderRadius: "20px",
        backgroundColor: "#1B1D28",
        padding: "24px",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
          height: "38px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            fontSize: "18px",
          }}
        >
          <Typography fontSize="18px">{currentSession?.name}</Typography>
          <Circle size={10} color="#6C65D9" weight="fill" />
          <span style={{ color: "#B1B5C9" }}>
            {moment(currentSession?.created_at).format("DD MMM YYYY")}
          </span>
        </div>
        <IconButton
          onClick={closeModal}
          sx={{
            "&:hover": {
              backgroundColor: "rgba(249, 249, 249, 0.06)",
            },
          }}
        >
          <X size={20} color="#F9F9F9" />
        </IconButton>
      </Box>
      {/* Body */}
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          height: "calc(100% - 54px)", // Adjust based on header height
        }}
      >
        {/* Left Box */}

        <Box
          sx={{
            minWidth: "350px",
            width: {
              lg: "600px",
              md: "auto",
              sm: "auto",
              xs: "auto",
            },
            flexShrink: {
              lg: 0,
              md: 1,
              sm: 1,
              xs: 1,
            },
          }}
        >
          {/* Right box content */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: "12px", color: "#606479" }}>
              Version Timestamp
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#606479" }}>
              Session Timestamp
            </Typography>
          </Box>
          <Box sx={{ height: "calc(100% - 128px)", overflowY: "auto" }}>
            {versions.length > 0 &&
              versions.map((eachVersion) => (
                <Box
                  id={`version-${eachVersion.id}`}
                  key={eachVersion.id}
                  sx={{
                    opacity: eachVersion.id === currentVersion?.id ? 1 : 0.3,
                  }}
                >
                  <Typography
                    sx={{ fontSize: "13px", color: "#ECECEC", mt: 2, mb: 2 }}
                  >
                    {eachVersion.trackable_name}
                  </Typography>
                  {eachVersion.linked_transcriptions.map(
                    (eachTranscription) => (
                      <VRSessionTranscript
                        key={eachTranscription.Index}
                        transcription={eachTranscription}
                        seekAudio={seekAudio}
                        fileUrls={eachVersion.file_urls}
                        player={videoPlayerRef}
                      />
                    ),
                  )}
                </Box>
              ))}
          </Box>
          <Box
            sx={{
              height: "90px",
              backgroundColor: "#000000",
              borderRadius: "8px",
              padding: "8px",
            }}
          >
            <Typography
              sx={{ fontSize: "14px", color: "#ECECEC", marginBottom: "6px" }}
            >
              Session Audio
            </Typography>
            {currentSession?.file_urls && (
              <AudioControls audioRef={audioRef} audioUrl={currentAudioUrl} />
            )}
          </Box>
        </Box>

        {/* Right Box */}
        <Box
          sx={{
            minWidth: "600px",
            flex: 1, // Takes remaining space
          }}
        >
          {/* Left box content */}
          <Typography sx={{ fontSize: "14px", color: "#B1B5C9" }}>
            Version Reviewed
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              height: "38px",
              marginBottom: "12px",
              marginTop: "4px",
            }}
          >
            <Select
              key={`select-${currentVersion?.id || "empty"}`}
              size="small"
              labelId="select-label"
              id="select-demo"
              value={currentVersion ? String(currentVersion.id) : ""}
              displayEmpty
              onChange={(e) => {
                const selectedId = Number(e.target.value);
                const foundVersion = versions.find(
                  (version) => version.id === selectedId,
                );
                if (foundVersion) {
                  setCurrentVersion(foundVersion);
                }
              }}
              sx={{
                width: "300px",
                backgroundColor: "#252A38",
                borderRadius: "5px",
                color: "#F9F9F9",
                outline: "none",
                fontSize: {
                  xs: "8px",
                  sm: "8px",
                  md: "10px",
                  lg: "12px",
                  xl: "14px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                //dropdown icon
                "& .MuiSelect-icon": {
                  color: "#F9F9F9",
                },
                "& .MuiSelect-iconOpen": {
                  transform: "rotate(180deg)",
                },
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: "300px",
                    overflow: "auto",
                    backgroundColor: "#252A38",
                    color: "#F9F9F9",
                  },
                },
              }}
            >
              {versions.length > 0 &&
                versions.map((version) => (
                  <MenuItem
                    key={version.id}
                    value={String(version.id)}
                    sx={{
                      fontSize: {
                        xs: "8px",
                        sm: "8px",
                        md: "10px",
                        lg: "12px",
                        xl: "14px",
                      },
                      color: "#F9F9F9",
                    }}
                  >
                    {version.trackable_name}
                  </MenuItem>
                ))}
            </Select>
            <Button
              disabled={currentVersion?.index_in_session === 0}
              onClick={() => {
                setCurrentVersion(
                  versions[
                    versions.findIndex(
                      (version) =>
                        version.index_in_session ===
                        currentVersion?.index_in_session - 1,
                    )
                  ],
                );
              }}
              sx={{
                minWidth: "34px !important",
                height: "36px !important",
                borderRadius: "10px",
                bgcolor: "#252A38",
                boxSizing: "border-box",
                "&:hover": {
                  backgroundColor: "rgba(249, 249, 249, 0.04)",
                },
              }}
            >
              <CaretLeft size={14} color="#fff" />
            </Button>
            <Button
              disabled={
                versions.findIndex(
                  (version) => version.id === currentVersion?.id,
                ) ===
                versions.length - 1
              }
              onClick={() => {
                setCurrentVersion(
                  versions[
                    versions.findIndex(
                      (version) =>
                        version.index_in_session ===
                        currentVersion.index_in_session + 1,
                    )
                  ],
                );
              }}
              sx={{
                height: "36px !important",
                minWidth: "34px !important",
                borderRadius: "10px",
                bgcolor: "#252A38",
                boxSizing: "border-box",
                "&:hover": {
                  backgroundColor: "rgba(249, 249, 249, 0.04)",
                },
              }}
            >
              <CaretRight size={14} color="#fff" />
            </Button>
          </Box>
          {currentVersion && versions.length > 0 && (
            <VersionVideoPlayer
              currentVersion={currentVersion}
              versions={versions}
              setCurrentVersion={setCurrentVersion}
              player={videoPlayerRef}
            />
          )}
          <Grid2 container spacing={1} sx={{ height: "140px" }}>
            {allFilters.map((filter) => {
              return (
                <Grid2 size={{ xs: 4 }} key={filter.id}>
                  <Box
                    sx={{
                      bgcolor: "#252A38",
                      height: "65px",
                      padding: "10px",
                      borderRadius: "5px",
                      boxSizing: "border-box",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <Checkbox
                          color="success"
                          checked
                          value={true}
                          size="small"
                          sx={{
                            height: "14px",
                            width: "14px",
                          }}
                        />

                        <Typography sx={{ fontSize: "12px", color: "#F9F9F9" }}>
                          {filter.name}
                        </Typography>
                      </Box>
                      {filter.icon}
                    </Box>
                    <Typography sx={{ fontSize: "14px", color: "#F9F9F9" }}>
                      {filter.count}
                    </Typography>
                  </Box>
                </Grid2>
              );
            })}
          </Grid2>
        </Box>
      </Box>
    </Box>
  );
}

export default VRSessionModal;
